import '../sentry.client.config';
import '../styles/globals.css';
import '../styles/page-container.css';
import type { AppProps } from 'next/app';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.min.css';

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { Provider } from 'jotai';
import dynamic from 'next/dynamic';
import React, { useMemo } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import {
  ApiError,
  Policy,
  AppNameProvider,
  Forbidden,
  ErrorFallback,
  Layout,
} from 'ui';
import useUser from '../auth/useUser';
import { APP_NAME } from '../appName';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Avatar,
  CssBaseline,
  GlobalStyles,
  ThemeProvider,
} from '@mui/material';
import { agGridMuiGlobalStyles } from '../styles/theme/agGridTheme';
import { theme } from '../styles/theme';
import { Inventory2Outlined, TuneOutlined } from '@mui/icons-material';
import userHasPermission from '../utils/functions/userHasPermission';
import { RolePermission } from 'desk-common';

// We load this component dynamically to disable SSR on it because it depends on the value of the window object.
const AuthContextProvider = dynamic(
  () => {
    return import('../auth/AuthContext');
  },
  { ssr: false }
);

const errorFunction = (error: unknown) => {
  if (error instanceof ApiError) {
    console.error(error, {
      errorCode: error.errorCode,
      appName: error.appName,
      ctx: error.ctx,
      cause: error.cause,
      requestId: error.requestId,
      errorId: error.errorId,
    });
    toast.error(
      `Error: ${error.message}\nCódigo: ${error.errorCode}\n[${error.errorId}]`,
      {
        duration: 5000,
      }
    );
    return;
  }

  let realError = error;
  if (!(error instanceof Error)) {
    realError = new Error(JSON.stringify(error));
  }

  console.error(realError);
  toast.error(`Error: ${(realError as Error).message}`);
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: errorFunction,
  }),
  mutationCache: new MutationCache({
    onError: errorFunction,
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

type ExtendedAppProps = AppProps & {
  Component: AppProps['Component'] & { skipAuth?: boolean };
};

function ComponentWrapper({ Component, pageProps }: ExtendedAppProps) {
  const user = useUser();

  const asideMenuOptions = useMemo(() => {
    const validRoles = [RolePermission.useMasters];

    const canSeeMasters = userHasPermission(user, validRoles, 'ANY');

    const menu = [
      {
        title: 'Icono',
        icon: (
          <Avatar alt="Icon" src="/icon.svg" sx={{ width: 80, height: 80 }} />
        ),
      },
      {
        title: 'Pedidos',
        path: '/orders',
        icon: <Inventory2Outlined color="primary" fontSize="large" />,
        titleStyle: {
          fontWeight: 'bold',
          color: 'inherit',
        },
        buttonStyle: {
          '&:hover': { color: theme.palette.primary.light },
          borderRadius: '12px',
          transition: 'all 0.3s',
        },
      },
    ];

    if (canSeeMasters) {
      menu.push({
        title: 'Maestros',
        path: '/masters',
        icon: <TuneOutlined color="primary" fontSize="large" />,
        titleStyle: {
          fontWeight: 'bold',
          color: 'inherit',
        },
        buttonStyle: {
          '&:hover': { color: theme.palette.primary.light },
          borderRadius: '12px',
          transition: 'all 0.3s',
        },
      });
    }

    return menu;
  }, [user]);

  if (Component.skipAuth) {
    return (
      <React.StrictMode>
        <Component {...pageProps} />
      </React.StrictMode>
    );
  }

  // The AuthContextProvider (and the keycloak-js library) does not play well with StrictMode's unmounting and remounting of the components.
  // Running AuthContextProvider inside StrictMode causes an infinite redirect loop when coming back from the idP.
  return (
    <AuthContextProvider clientId="monorepo">
      <Policy
        user={user}
        roles={['login']}
        fallback={
          <Forbidden
            text={'No tienes permiso para acceder a la aplicación'}
            userLogout={user?.actions?.logout}
            email={user?.email}
          />
        }
      >
        <React.StrictMode>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalStyles styles={agGridMuiGlobalStyles} />
            <Layout
              pageTitle=""
              userName={user?.name}
              userLogout={user?.actions?.logout}
              asideMenuOptions={asideMenuOptions}
              drawerWidth={150}
              enableGasesIcon={false}
              customHeaderStyle={{
                width: { sm: `calc(100% - ${150}px)` },
                ml: { sm: `${150}px` },
              }}
              customAsideMenuStyle={{
                '& .MuiDrawer-paper': {
                  width: 150,
                  boxSizing: 'border-box',
                  py: 0,
                },
              }}
              customFooterStyle={{
                boxShadow: 'none',
              }}
            >
              <Component {...pageProps} />
            </Layout>
          </ThemeProvider>
        </React.StrictMode>
      </Policy>
    </AuthContextProvider>
  );
}

function MyApp(props: ExtendedAppProps) {
  return (
    <ErrorBoundary FallbackComponent={() => <ErrorFallback />}>
      <AppNameProvider appName={APP_NAME}>
        <Provider>
          <Toaster />
          <QueryClientProvider client={queryClient}>
            <ComponentWrapper {...props} />
          </QueryClientProvider>
        </Provider>
      </AppNameProvider>
    </ErrorBoundary>
  );
}

export default MyApp;
